/**
 * Web Audio graph, procedural buffer bank and bounded cue router.
 *
 * The context is created during init but may remain suspended until the first user gesture.
 * All expensive synthesis buffers and persistent graph nodes are still built before the match,
 * avoiding a first-hit hitch when autoplay unlocks.
 */

import { clamp, DisposalBag, System, TICK_RATE } from '../core/index.js';
import {
  closeContext, ensureContext, getContext, isRunning,
} from './context.js';
import {
  makeBrownNoise, makeCrowdBed, makeGlottalPulse, makeImpulseResponse,
  makePinkNoise, makeTanhCurve, makeVelvetNoise, makeWhiteNoise, ShotBuilder,
} from './dsp.js';
import { cueFamily, renderCue } from './cues.js';

const VOICE_CAPACITY = 48;
const VOICE_NODE_CAPACITY = 18;

function resetVoice(voice) {
  for (let i = 0; i < voice.nodeCount; i++) {
    const node = voice.nodes[i];
    if (!node) continue;
    try {
      if (node.stop) node.stop();
    } catch {
      // A source that already ended cannot be stopped twice.
    }
    try {
      if (node.disconnect) node.disconnect();
    } catch {
      // A disconnected node is already clean.
    }
    voice.nodes[i] = null;
  }
  voice.nodeCount = 0;
  voice.endTime = 0;
  voice.family = '';
}

function setParam(param, value, now) {
  if (!param) return;
  param.cancelScheduledValues(now);
  param.setValueAtTime(value, now);
}

export class AudioSystem extends System {
  static id = 'audio';
  static deps = ['fx'];
  static budgetMs = 0.3;

  constructor() {
    super();
    this.bag = new DisposalBag('audio');
    this.ac = null;
    this.ctx = null;
    this.builder = new ShotBuilder();
    this.buffers = null;
    this.voices = null;
    this.master = null;
    this.sfx = null;
    this.voiceBus = null;
    this.ambience = null;
    this.music = null;
    this.reverbInput = null;
    this.reverbGain = null;
    this._unsubs = [];
    this._audioBase = 0;
    this._rng = null;
    this._ready = false;
    this._ambienceSources = [];
    this._ambienceStarted = false;
  }

  async init(ctx) {
    this.ctx = ctx;
    // Intentionally do not await: autoplay policy may keep the promise pending until a gesture.
    // `ensureContext` constructs the context synchronously, so the suspended graph can be baked.
    const unlocked = ensureContext({ latencyHint: 'interactive' });
    const ac = getContext();
    if (!ac) {
      ctx.log.info('audio: Web Audio unavailable; cue path degrades to silence');
      return;
    }
    this._setup(ac, ctx);
    unlocked.then((runningContext) => {
      if (runningContext && runningContext === this.ac && this.ctx) this._startAmbience();
    });
  }

  _setup(ac, ctx) {
    if (this._ready) return;
    this.ac = ac;
    this._rng = ctx.rng.fx.fork('audio');
    this._audioBase = ac.currentTime - ctx.time.simSeconds;

    this.master = this.bag.audioNode(ac.createGain());
    this.sfx = this.bag.audioNode(ac.createGain());
    this.voiceBus = this.bag.audioNode(ac.createGain());
    this.ambience = this.bag.audioNode(ac.createGain());
    this.music = this.bag.audioNode(ac.createGain());
    const compressor = this.bag.audioNode(ac.createDynamicsCompressor());
    compressor.threshold.value = -17;
    compressor.knee.value = 15;
    compressor.ratio.value = 5;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.18;
    const shaper = this.bag.audioNode(ac.createWaveShaper());
    shaper.curve = makeTanhCurve(2048, 1.45);
    shaper.oversample = '2x';

    this.sfx.connect(this.master);
    this.voiceBus.connect(this.master);
    this.ambience.connect(this.master);
    this.music.connect(this.master);
    this.master.connect(compressor).connect(shaper).connect(ac.destination);

    this.reverbInput = this.bag.audioNode(ac.createGain());
    const convolver = this.bag.audioNode(ac.createConvolver());
    this.reverbGain = this.bag.audioNode(ac.createGain());
    this.reverbInput.connect(convolver).connect(this.reverbGain).connect(this.master);

    this.buffers = {
      white: makeWhiteNoise(ac, this._rng, 2.2, 2),
      pink: makePinkNoise(ac, this._rng, 2.5),
      brown: makeBrownNoise(ac, this._rng, 3.0),
      velvet: makeVelvetNoise(ac, this._rng, 1.15, 1700),
      voice: makeGlottalPulse(ac, this._rng, {
        f0: 176, cycles: 96, oq: 0.58, jitter: 0.045, shimmer: 0.12,
      }).buffer,
      crowd: makeCrowdBed(ac, this._rng, { seconds: 4.2, grains: 1050 }),
    };
    convolver.buffer = makeImpulseResponse(ac, this._rng, {
      seconds: 1.15, rt60: 0.82, predelay: 0.009, earlyTaps: 13, damping: 0.62,
    });

    this.voices = ctx.pools.create(
      'audio-voices',
      () => ({
        nodes: new Array(VOICE_NODE_CAPACITY).fill(null),
        nodeCount: 0,
        endTime: 0,
        family: '',
      }),
      resetVoice,
      VOICE_CAPACITY,
    );

    this._prepareAmbience();
    this._applyMix();
    this._unsubs.push(ctx.events.on('audio:cue', (p) => this._onCue(p)));
    for (const key of [
      'audioMaster', 'audioSfx', 'audioMusic', 'audioAmbience', 'audioVoice',
    ]) {
      this._unsubs.push(ctx.config.onChange(key, () => this._applyMix()));
    }
    this._ready = true;
    ctx.log.info(`audio: procedural mix ready with ${VOICE_CAPACITY} bounded one-shot voices`);
  }

  _prepareAmbience() {
    const ac = this.ac;
    const makeLoop = (buffer, destination, filterType, frequency, gainValue, rate) => {
      const source = this.bag.audioNode(ac.createBufferSource());
      const filter = this.bag.audioNode(ac.createBiquadFilter());
      const gain = this.bag.audioNode(ac.createGain());
      source.buffer = buffer;
      source.loop = true;
      source.playbackRate.value = rate;
      filter.type = filterType;
      filter.frequency.value = frequency;
      filter.Q.value = 0.55;
      gain.gain.value = gainValue;
      source.connect(filter).connect(gain).connect(destination);
      this._ambienceSources.push(source);
      return source;
    };
    makeLoop(this.buffers.white, this.ambience, 'highpass', 1450, 0.052, 0.93);
    makeLoop(this.buffers.brown, this.ambience, 'lowpass', 430, 0.065, 0.72);
    makeLoop(this.buffers.pink, this.ambience, 'bandpass', 190, 0.035, 0.58);
    makeLoop(this.buffers.crowd, this.ambience, 'bandpass', 760, 0.045, 0.84);
  }

  _startAmbience() {
    if (this._ambienceStarted || !this.ac) return;
    this._ambienceStarted = true;
    const now = this.ac.currentTime;
    for (const source of this._ambienceSources) source.start(now);
  }

  _applyMix() {
    if (!this.ac || !this.master) return;
    const now = this.ac.currentTime;
    const mix = this.ctx.config.audio;
    setParam(this.master.gain, mix.master, now);
    setParam(this.sfx.gain, mix.sfx, now);
    setParam(this.voiceBus.gain, mix.voice, now);
    setParam(this.ambience.gain, mix.ambience, now);
    setParam(this.music.gain, mix.music, now);
    setParam(this.reverbGain.gain, 0.16 * mix.sfx, now);
  }

  _onCue(p) {
    if (!this._ready || !this.ac || !this.voices || !isRunning()) {
      if (this.ctx) this.ctx.metrics.counter('audio.cuesDroppedLocked', 1);
      return;
    }
    if (p.cue === 'music-intensity') return;
    const family = cueFamily(p.cue);
    const voice = this.voices.acquire();
    voice.family = family;
    const simTime = p.tick / TICK_RATE;
    let expected = this._audioBase + simTime;
    // The simulation may have advanced while autoplay kept the audio clock suspended, and tools
    // may seek/reset the simulation clock. Re-anchor at the first audible cue after either case.
    if (Math.abs(expected - this.ac.currentTime) > 0.25) {
      this._audioBase = this.ac.currentTime - simTime;
      expected = this.ac.currentTime;
    }
    const when = Math.max(this.ac.currentTime + 0.003, expected);
    const pan = clamp(p.x / 5.6, -0.78, 0.78);
    const destination = family === 'voice' || family === 'announce' ? this.voiceBus : this.sfx;
    const duration = renderCue(this.builder.bind(this.ac, voice, this.buffers, this._rng), p.cue, {
      when,
      gain: p.gain,
      pitch: p.pitch,
      pan,
      destination,
      send: family === 'ui' ? null : this.reverbInput,
    });
    voice.endTime = when + duration + 0.08;
    if (this.builder.overflow > 0) {
      this.ctx.metrics.counter('audio.voiceNodeOverflow', this.builder.overflow);
      this.builder.overflow = 0;
    }
    if (this.ctx.config.get('audioDucking')
        && (family === 'impact' || family === 'throw' || family === 'voice')) {
      this._duck(when, family === 'impact' ? 0.72 : 0.8, duration);
    }
  }

  _duck(when, amount, duration) {
    const base = this.ctx.config.get('audioAmbience');
    const gain = this.ambience.gain;
    gain.cancelScheduledValues(when);
    gain.setValueAtTime(gain.value, when);
    gain.linearRampToValueAtTime(base * amount, when + 0.008);
    gain.exponentialRampToValueAtTime(Math.max(0.0001, base), when + Math.max(0.16, duration + 0.1));
  }

  update(_dt, ctx) {
    if (!this._ready || !this.voices || !this.ac) return;
    const now = this.ac.currentTime;
    for (let i = this.voices.liveCount - 1; i >= 0; i--) {
      const voice = this.voices.itemAt(this.voices.liveIndexAt(i));
      if (voice.endTime > 0 && now >= voice.endTime) this.voices.release(voice);
    }
    ctx.metrics.setCounter('audio.voices', this.voices.liveCount);
    ctx.metrics.setCounter('audio.running', isRunning() ? 1 : 0);
  }

  state() {
    return {
      ready: this._ready,
      running: isRunning(),
      voices: this.voices ? this.voices.liveCount : 0,
      capacity: VOICE_CAPACITY,
      buffers: this.buffers ? Object.keys(this.buffers).length : 0,
    };
  }

  async dispose() {
    for (const off of this._unsubs) off();
    this._unsubs.length = 0;
    if (this.ctx) this.ctx.pools.destroy('audio-voices');
    this.bag.disposeAll();
    await closeContext();
    this.ac = null;
    this.ctx = null;
    this.buffers = null;
    this.voices = null;
    this._ambienceSources.length = 0;
    this._ambienceStarted = false;
    this._ready = false;
  }
}
